import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "../styles/FieldSelector.css";

function FieldSelector() {
  const { databaseId } = useParams();
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedFields, setSelectedFields] = useState({
    frontField: "",
    backFields: [],
  });

  useEffect(() => {
    const fetchDatabaseSchema = async () => {
      try {
        const response = await axios.get(
          `/api/database-schema.php?databaseId=${databaseId}`
        );
        setFields(response.data);
        // Set first field as default front field if available
        if (response.data.length > 0) {
          setSelectedFields((prev) => ({
            ...prev,
            frontField: response.data[0].name,
          }));
        }
      } catch (err) {
        setError(
          "Failed to fetch database schema. Please check your database ID and try again."
        );
        console.error("Error fetching schema:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDatabaseSchema();
  }, [databaseId]);

  const handleFrontFieldChange = (e) => {
    setSelectedFields((prev) => ({
      ...prev,
      frontField: e.target.value,
    }));
  };

  const handleBackFieldToggle = (fieldName) => {
    setSelectedFields((prev) => {
      const newBackFields = [...prev.backFields];
      if (newBackFields.includes(fieldName)) {
        return {
          ...prev,
          backFields: newBackFields.filter((field) => field !== fieldName),
        };
      } else {
        return {
          ...prev,
          backFields: [...newBackFields, fieldName],
        };
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedFields.frontField) {
      setError("Please select a front field");
      return;
    }
    if (selectedFields.backFields.length === 0) {
      setError("Please select at least one back field");
      return;
    }

    // Store selected fields in localStorage for FlashcardViewer
    localStorage.setItem("flashcardFields", JSON.stringify(selectedFields));

    // Navigate to flashcard viewer
    navigate(`/flashcards/${databaseId}`);
  };

  if (loading) return <div className="loading">Loading database schema...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="field-selector">
      <h2>Select Flashcard Fields</h2>
      <form onSubmit={handleSubmit}>
        <div className="field-group">
          <h3>Front Side Field</h3>
          <p>Select the field to display on the front of your flashcards:</p>
          <select
            value={selectedFields.frontField}
            onChange={handleFrontFieldChange}
          >
            {fields.map((field) => (
              <option key={field.id} value={field.name}>
                {field.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field-group">
          <h3>Back Side Fields</h3>
          <p>Select fields to display on the back of your flashcards:</p>
          <div className="checkbox-group">
            {fields.map((field) => (
              <div key={field.id} className="checkbox-item">
                <input
                  type="checkbox"
                  id={`back-${field.id}`}
                  checked={selectedFields.backFields.includes(field.name)}
                  onChange={() => handleBackFieldToggle(field.name)}
                  disabled={field.name === selectedFields.frontField}
                />
                <label htmlFor={`back-${field.id}`}>{field.name}</label>
              </div>
            ))}
          </div>
        </div>

        {error && <p className="error">{error}</p>}
        <div className="button-group">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="secondary-button"
          >
            Back
          </button>
          <button type="submit" className="primary-button">
            Create Flashcards
          </button>
        </div>
      </form>
    </div>
  );
}

export default FieldSelector;
